import React, { useRef, useEffect, useState, useMemo } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import CardMedia from "@material-ui/core/CardMedia";
import withStyles from "@material-ui/core/styles/withStyles";
import Typography from "@material-ui/core/Typography";
import moment from "moment";
import { CardHeader, Checkbox, FormControlLabel } from "@material-ui/core";
import { useApolloClient } from "@apollo/client";
import AttendingUsers from "./AttendingUsers";
import UserPicker from "../../../components/common/AutoSuggest";
import updateEventMutation from "../../../graphql/event/mutation/update-event";
import getSingleEventDetails from "../../../graphql/event/query/single-event-details";
import verifyEventUsersMutation from "../../../graphql/event/mutation/verify-event-users";
import companyUserSearchV2Query from "../../../graphql/reports/company-user-search-v2";
import searchUsersV2Query from "../../../graphql/reports/search-users-v2";
import CurrentUserContext from "../../../containers/CurrentUser/CurrentUserContext";

const SUGGESTION_LIMIT = 25;

const R = require("ramda");

const styles = (theme) => ({
  cardMedia: {
    paddingTop: "56.25%", // 16:9
    position: "relative",
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    flexGrow: 1,
    alignItems: "flex-start",
    paddingTop: 0,
  },
  timeCaption: {
    display: "flex",
    justifyContent: "space-between",
  },
  count: {
    position: "absolute",
    right: 16,
    bottom: 16,
    color: "white",
  },
  scrollbarThumb: {
    background: "linear-gradient(0deg, #0098f0 0%, #00f2c3 100%)",
    opacity: 0.5,
    borderRadius: 10,
  },
});

// Be wary of infinite loops when using event and setInternalEvent

function EventModal(props) {
  const { event, setInternalEvent } = props;
  const theme = useTheme();
  const userPickerRef = useRef();
  const client = useApolloClient();
  // Term-level memo of the last searches: backspacing/retyping a prefix is
  // instant instead of another round-trip.
  const searchCacheRef = useRef(new Map());

  const { classes, refetch } = props;
  const [includeExternalUsers, setIncludeExternalUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const currentUserContext = React.useContext(CurrentUserContext);
  const isAdmin = !!(currentUserContext?.currentUser?.roles || []).includes(
    "admin",
  );
  const eventCompanyId = event?.company?._id
    ? event.company._id
    : localStorage.getItem("company_id");

  const _internalRefetch = () => {
    console.log("Refetching");
    (async () => {
      // Pulling many events can introduce stale data
      // So we refetch the event details after 5 seconds
      setTimeout(() => {
        refetch();
      }, 1500);
    })();
    if (!event._id) {
      return;
    }
    console.log("Refetching event details");
    setLoading(true);
    client
      .query({
        query: getSingleEventDetails,
        variables: { _id: event._id },
      })
      .then(({ error, data }) => {
        const eventResp = data?.getOneEventDetails;
        if (!eventResp) {
          return;
        }
        console.log("Refetched single event details", {
          eventResp,
        });

        setLoading(false);
        setInternalEvent(eventResp);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const internalRefetch = () => {
    // Changes could reflect later than commited.
    setTimeout(() => {
      _internalRefetch();
    }, 5000);
  };

  function handleClickOpen() {
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  const currentEventUsers = useMemo(() => {
    return event?.users?.map((user) => user?._id).filter(Boolean) || [];
  }, [event]);

  /**
   * Server-side suggestion search.
   *
   * Previously the modal pulled the company's FULL user documents (~2.3 MB for
   * Cato) and, with "external users" on, every user in the database (15k+ full
   * documents — minutes). The picker then filtered that array in the browser,
   * so typing showed nothing until the whole payload arrived. Now each
   * keystroke (debounced) asks the server for ~25 projected rows on an indexed
   * prefix match, scoped to this company unless external users are enabled.
   */
  const searchAttendees = async (term) => {
    const scope = includeExternalUsers && isAdmin ? "all" : String(eventCompanyId || "");
    const key = `${scope}::${String(term || "").trim().toLowerCase()}`;
    const cache = searchCacheRef.current;

    let raw = cache.get(key);
    if (!raw) {
      if (scope === "all") {
        const { data } = await client.query({
          query: searchUsersV2Query,
          variables: { term, limit: SUGGESTION_LIMIT },
          fetchPolicy: "network-only",
        });
        raw = data?.searchUsersV2?.users || [];
      } else if (!eventCompanyId) {
        raw = [];
      } else {
        const { data } = await client.query({
          query: companyUserSearchV2Query,
          variables: {
            companyId: eventCompanyId,
            term,
            limit: SUGGESTION_LIMIT,
          },
          fetchPolicy: "network-only",
        });
        raw = data?.companyUserSearchV2?.users || [];
      }
      if (cache.size > 60) cache.clear();
      cache.set(key, raw);
    }

    // Filtered per call (not cached) so a just-added attendee disappears
    // immediately when the same term is searched again.
    const alreadyAttending = new Set(currentEventUsers);
    return raw.filter((u) => u && u._id && !alreadyAttending.has(u._id));
  };

  const handleAddUser = () => {
    const picker = userPickerRef.current;
    const userToAdd = selectedUser;
    if (!userToAdd || !userToAdd._id) {
      return;
    }
    const updatedEventUsers = [...currentEventUsers, userToAdd._id];
    setLoading(true);
    setInternalEvent(() => {
      // Note that by default, we are adding the user to both users and verifiedUsers
      return {
        ...event,
        users: [...(event.users || []), userToAdd],
        verifiedUsers: [...(event.verifiedUsers || []), userToAdd],
      };
    });
    client
      .mutate({
        mutation: updateEventMutation,
        variables: {
          _id: event._id,
          company: eventCompanyId,
          users: updatedEventUsers,
        },
      })
      .then((value) => {
        setLoading(false);
        setSelectedUser(null);
        if (picker?.clear) {
          picker.clear();
        } else if (picker) {
          picker.onChange(null, { newValue: "" });
          picker.onSuggestionsClearRequested();
        }
        internalRefetch();
        setInternalEvent(value.data.updateEvent);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  // Clear a stale selection when the search scope changes.
  useEffect(() => {
    setSelectedUser(null);
  }, [includeExternalUsers]);

  return (
    <div>
      <div style={{ cursor: "pointer" }} onClick={handleClickOpen}>
        {props.children}
      </div>
      <Dialog
        fullWidth
        fullScreen={fullScreen}
        maxWidth="sm"
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
        style={
          isLoading
            ? {
                pointerEvents: "none",
                opacity: "0.8",
                transition: "opacity 0.4s ease",
              }
            : { transition: "opacity 0.4s ease" }
        }
        scroll="body"
      >
        <CardMedia
          className={classes.cardMedia}
          image={event.image}
          title={event.title}
          src=""
        >
          <Typography className={classes.count} variant="h6">
            {(() => {
              const usersCount = event?.users?.length || 0;
              const validPending = (event?.pendingApprovals || []).filter((p) =>
                moment().isBefore(p.expiresAt),
              );
              const pendingCount = validPending.length;
              return (
                <>
                  {`${usersCount + pendingCount}/${event.totalSpots} spots `}
                  {pendingCount > 0 && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        display: "block",
                        textAlign: "right",
                      }}
                    >
                      ({pendingCount} pending)
                    </span>
                  )}
                </>
              );
            })()}
          </Typography>
        </CardMedia>
        <CardHeader
          style={{ textTransform: "capitalize" }}
          title={`${event.title} with ${R.pathOr("Unknown", ["firstName"])(
            event.instructor,
          )} ${R.pathOr("", ["lastName"])(event.instructor)}`}
          subheader={`${moment(event.date).format(" MMMM Do, HH:mm")} at ${
            event.location
          }`}
        />

        <DialogContent className={classes.cardContent}>
          <Typography>{event.description}</Typography>
          {isLoading ? (
            <Typography>Loading Please Do Not Modify...</Typography>
          ) : (
            <div></div>
          )}
          <AttendingUsers
            eventUsers={event.users}
            verifiedUsers={event.verifiedUsers}
            refetch={internalRefetch}
            onVerify={(user) => {
              const oldVerifiedUsers = event.verifiedUsers || [];
              const oldVerifiedUsersId = oldVerifiedUsers.map(
                (user) => user._id,
              );
              const newVerifiedUsers = oldVerifiedUsersId.includes(user._id)
                ? oldVerifiedUsers.filter((userObj) => userObj._id !== user._id)
                : [...oldVerifiedUsers, user];
              setLoading(true);
              setInternalEvent(() => {
                console.log("Setting verifiedUsers event", {
                  event,
                  newVerifiedUsers,
                });
                return { ...event, verifiedUsers: newVerifiedUsers };
              });
              client
                .mutate({
                  mutation: verifyEventUsersMutation,
                  variables: {
                    _id: event._id,
                    usersIds: newVerifiedUsers.map((user) => user._id),
                  },
                })
                .then((value) => {
                  setLoading(false);
                  console.log("Verify user calling setInternalEvent");
                  internalRefetch();
                });
            }}
            onRemove={(user) => {
              const updatedUsers = event?.users?.filter(
                (value) => value._id !== user._id,
              );
              // We also should remove from verified users
              const updatedVerifiedUsers = event?.verifiedUsers?.filter(
                (value) => value._id !== user._id,
              );
              setLoading(true);
              setInternalEvent(() => {
                console.log("Setting remove user event", {
                  event,
                  updatedUsers,
                  updatedVerifiedUsers,
                });
                return {
                  ...event,
                  users: updatedUsers,
                  verifiedUsers: updatedVerifiedUsers,
                };
              });
              client
                .mutate({
                  mutation: updateEventMutation,
                  variables: {
                    _id: event._id,
                    users: updatedUsers.map((user) => user._id),
                    company: eventCompanyId,
                  },
                })
                .then((value) => {
                  setLoading(false);
                  console.log("Remove user calling setInternalEvent");
                  internalRefetch();
                  setInternalEvent(value.data.updateEvent);
                });
            }}
            event={event}
            style={{ marginBottom: 20 }}
          >
            <div
              style={{
                marginLeft: 26,
                marginTop: 10,
              }}
            >
              {isAdmin && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeExternalUsers}
                      onChange={(e) => setIncludeExternalUsers(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Add external users too"
                />
              )}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: 10,
                }}
              >
                <UserPicker
                  ref={userPickerRef}
                  onSearch={searchAttendees}
                  searchScope={includeExternalUsers}
                  onSelected={(user) => setSelectedUser(user)}
                />
                <Button
                  onClick={handleAddUser}
                  disabled={!selectedUser}
                  color="primary"
                >
                  Add
                </Button>
              </div>
            </div>
          </AttendingUsers>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            style={{ paddingBottom: 50 }}
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default withStyles(styles)(EventModal);
